import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { siteData } from '../data';
import { CheckCircle2, ArrowRight, Phone, Mail } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = siteData.services.find(s => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Siden blev ikke fundet</h1>
          <p className="text-gray-600 mb-8">Vi kunne ikke finde den ydelse, du leder efter.</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Gå til forsiden
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <Helmet>
        <title>{`${service.title} | VHservice`}</title>
        <meta name="description" content={service.description} />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-24 bg-blue-950 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-950 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {service.title}
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              {service.description}
            </p>
            <a
              href="#contact"
              className="inline-flex items-center px-8 py-4 md:px-5 md:py-3 md:text-base lg:px-6 lg:py-3 lg:text-base xl:px-8 xl:py-4 xl:text-lg bg-white text-blue-900 font-bold rounded-lg hover:bg-blue-50 transition-all transform hover:-translate-y-1 shadow-lg"
            >
              Få et tilbud nu
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-gray-900">
                Professionel løsning til {service.slug === 'erhverv' ? 'erhvervslivet' : 'dit hjem'}
              </h2>
              <div className="prose prose-lg text-gray-600 max-w-none">
                {service.fullContent.split('\n').map((paragraph, index) => (
                  paragraph.trim() ? (
                    <p key={index} className="mb-4">
                      {paragraph.trim().startsWith('-') ? (
                        <span className="flex items-start">
                          <CheckCircle2 className="w-6 h-6 text-black mr-3 mt-1 flex-shrink-0" />
                          <span>{paragraph.trim().substring(1).trim()}</span>
                        </span>
                      ) : (
                        paragraph.trim()
                      )}
                    </p>
                  ) : null
                ))}
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 pt-8">
                <div className="p-6 bg-white rounded-xl border border-slate-100 shadow-md">
                  <h4 className="font-bold text-slate-900 mb-2">Pålidelig service</h4>
                  <p className="text-sm text-slate-600">Vi overholder altid vores aftaler og kommer til tiden.</p>
                </div>
                <div className="p-6 bg-white rounded-xl border border-slate-100 shadow-md">
                  <h4 className="font-bold text-slate-900 mb-2">Kvalitetsgaranti</h4>
                  <p className="text-sm text-slate-600">Vi er først tilfredse, når dine ruder står helt skarpt.</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="sticky top-24">
                <div className="rounded-none overflow-hidden shadow-2xl mb-8">
                  <img
                    src={
                      service.slug === 'erhverv' 
                        ? "https://assets.auxodev.dk/assets/a63bb5b8-c11e-4d14-812c-e833a8e6def1/a57d6770-6cd2-476e-8bb8-496d6676b42a.jpg" 
                        : service.slug === 'abonnement'
                        ? "https://assets.auxodev.dk/assets/a63bb5b8-c11e-4d14-812c-e833a8e6def1/e964c3d9-bab0-4720-8323-a185e030e349.jpg"
                        : "https://assets.auxodev.dk/assets/a63bb5b8-c11e-4d14-812c-e833a8e6def1/c265ac61-7a93-43a3-9889-6fddc9de98b9.jpg"
                    }
                    alt={service.title}
                    className="w-full h-full object-cover aspect-4/3"
                  />
                </div>
                
                <div className="bg-blue-700 rounded-none p-8 text-white shadow-sm">
                  <h3 className="text-xl font-bold mb-6 text-white">Har du spørgsmål?</h3>
                  <div className="space-y-4">
                    <a
                      href={`tel:${siteData.contact.phone}`}
                      className="flex items-center space-x-4 p-4 rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition-all group"
                    >
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-blue-700 transition-colors text-white">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-100 font-medium">Ring til os</p>
                        <p className="font-bold text-white">{siteData.contact.phone}</p>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <ContactForm />
    </div>
  );
};

export default ServiceDetailPage;
